/**
 * Billing cadence for `recurring_transactions` (monthly | quarterly | annual).
 *
 * Pure — no I/O — so the sync cron, the retry cron, the Meridian tools and the
 * MRR readers all share ONE answer to "is this row due, and what is it worth per
 * month".
 *
 * ## The model
 *
 * - `billing_cadence` — how often the row is invoiced. `monthly` (the column
 *   default, and what every pre-existing row is) means "every month", exactly as
 *   before this column existed. `amount` is the amount PER BILLING PERIOD.
 * - `next_bill_on` — for quarterly/annual rows only (DB CHECK: required for
 *   them, ignored for monthly): the next billing date. A row is DUE in the month
 *   that contains it. The day of month is informational; the crons run on the
 *   1st and select by month.
 *
 * ## Why "due in the month containing next_bill_on" and `<=`, not `==`
 *
 * If a month's run is missed (QuickBooks down, cron outage), an `==` rule would
 * skip that period forever — silently dropping a quarter or a year of revenue.
 * `<=` bills it on the next run instead. After a successful sync the anchor
 * jumps to the first period strictly AFTER the month just billed, so a catch-up
 * bills ONE invoice, never the backlog of periods back-to-back.
 *
 * ## Month-end / leap-year
 *
 * Advancement uses `addCalendarMonths` (clamps to the last day of the target
 * month, never overflows: Jan 31 + 3 months = Apr 30, Nov 30 + 3 = Feb 28/29).
 * Because only the current anchor is stored, a 29th–31st anchor can drift to an
 * earlier day after a short month (Jan 31 -> Apr 30 -> Jul 30). Month-level
 * selection makes that harmless for billing; prefer the 1st for new rows.
 */

import { addCalendarMonths, isIsoDate } from "@west-tennessee-consulting/business-date";
import { normalizeToMrr } from "@west-tennessee-consulting/recurring-revenue";

export const RECURRING_BILLING_CADENCES = ["monthly", "quarterly", "annual"] as const;

export type RecurringBillingCadence = (typeof RECURRING_BILLING_CADENCES)[number];

export const DEFAULT_RECURRING_BILLING_CADENCE: RecurringBillingCadence = "monthly";

/** How many calendar months one billing period covers. */
export const MONTHS_PER_BILLING_PERIOD: Readonly<Record<RecurringBillingCadence, number>> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
};

export function isRecurringBillingCadence(
  value: string | null | undefined
): value is RecurringBillingCadence {
  return RECURRING_BILLING_CADENCES.includes(value as RecurringBillingCadence);
}

/**
 * Read a stored cadence. A missing value (null/undefined — a legacy row, or a
 * select that predates the column) is `monthly`, the behaviour those rows have
 * always had. An UNRECOGNISED string is `null`: the caller must refuse the row
 * rather than quietly bill it monthly (which would over-bill an annual client).
 */
export function readBillingCadence(raw: string | null | undefined): RecurringBillingCadence | null {
  if (raw === null || raw === undefined || raw === "") return DEFAULT_RECURRING_BILLING_CADENCE;
  return isRecurringBillingCadence(raw) ? raw : null;
}

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMonthKey(value: string): boolean {
  return MONTH_KEY.test(value);
}

/** `YYYY-MM-DD` -> `YYYY-MM`. Caller must pass a valid ISO date. */
export function monthOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** Months since year 0 for a `YYYY-MM` key — for exact month arithmetic. */
function monthIndex(monthKey: string): number {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  return year * 12 + (month - 1);
}

/** The row fields the due check reads. */
export interface BillingScheduleRow {
  billing_cadence?: string | null;
  next_bill_on?: string | null;
  sync_status: string;
  last_synced_month: string | null;
}

export type DueReason =
  | "monthly_due"
  | "monthly_already_synced"
  | "period_due"
  | "period_not_yet_due"
  | "period_already_billed"
  | "missing_next_bill_on"
  | "invalid_next_bill_on"
  | "unknown_cadence";

export interface DueDecision {
  due: boolean;
  reason: DueReason;
}

/**
 * Is this row to be invoiced in `currentMonth` (`YYYY-MM`)?
 *
 * MONTHLY rows use the exact predicate the sync has always used
 * (`pending` OR `last_synced_month !== currentMonth`) — a row with no cadence at
 * all takes this branch, so legacy behaviour is byte-for-byte unchanged.
 *
 * QUARTERLY/ANNUAL rows additionally need their `next_bill_on` month to have
 * arrived. `pending` alone must NOT make them due: a brand-new annual row is
 * `pending` and its first bill may be a year out.
 */
export function evaluateRecurringDue(row: BillingScheduleRow, currentMonth: string): DueDecision {
  if (!isMonthKey(currentMonth)) {
    throw new Error(`evaluateRecurringDue: currentMonth must be YYYY-MM, got "${currentMonth}"`);
  }
  const cadence = readBillingCadence(row.billing_cadence);
  if (cadence === null) return { due: false, reason: "unknown_cadence" };

  if (cadence === "monthly") {
    const due = row.sync_status === "pending" || row.last_synced_month !== currentMonth;
    return { due, reason: due ? "monthly_due" : "monthly_already_synced" };
  }

  const nextBillOn = row.next_bill_on;
  if (nextBillOn === null || nextBillOn === undefined || nextBillOn === "") {
    return { due: false, reason: "missing_next_bill_on" };
  }
  if (!isIsoDate(nextBillOn)) return { due: false, reason: "invalid_next_bill_on" };

  const dueMonth = monthOf(nextBillOn);
  if (monthIndex(dueMonth) > monthIndex(currentMonth)) {
    return { due: false, reason: "period_not_yet_due" };
  }

  // Safety net: a `synced` row already stamped at/after its own due month was
  // billed for this period but the anchor never advanced (a partial write).
  // Re-billing it would double-charge a year, so refuse — loudly, via the reason
  // — rather than bill. An admin re-queue (`pending`) or a failure (`failed`) is
  // a deliberate signal and is not second-guessed here.
  if (
    row.sync_status === "synced" &&
    row.last_synced_month !== null &&
    isMonthKey(row.last_synced_month) &&
    monthIndex(row.last_synced_month) >= monthIndex(dueMonth)
  ) {
    return { due: false, reason: "period_already_billed" };
  }

  return { due: true, reason: "period_due" };
}

/**
 * The `next_bill_on` to store after a row was successfully invoiced in
 * `billedMonth` — or `null` for monthly rows (no anchor to move).
 *
 * Returns the first anchor strictly AFTER `billedMonth`, stepping from the
 * CURRENT anchor in whole periods. One jump from the original anchor (not a loop
 * of clamped steps) so a catch-up across several missed periods clamps the day
 * once. THROWS on a quarterly/annual row with no valid anchor: writing a guess
 * would mis-schedule revenue.
 */
export function nextBillOnAfterSync(
  cadenceRaw: string | null | undefined,
  currentNextBillOn: string | null | undefined,
  billedMonth: string
): string | null {
  const cadence = readBillingCadence(cadenceRaw);
  if (cadence === null) {
    throw new Error(`nextBillOnAfterSync: unknown billing cadence "${String(cadenceRaw)}"`);
  }
  if (cadence === "monthly") return null;
  if (!isMonthKey(billedMonth)) {
    throw new Error(`nextBillOnAfterSync: billedMonth must be YYYY-MM, got "${billedMonth}"`);
  }
  if (typeof currentNextBillOn !== "string" || !isIsoDate(currentNextBillOn)) {
    throw new Error(`nextBillOnAfterSync: ${cadence} row has no valid next_bill_on`);
  }

  const step = MONTHS_PER_BILLING_PERIOD[cadence];
  const monthsBehind = monthIndex(billedMonth) - monthIndex(monthOf(currentNextBillOn));
  // monthsBehind < 0 (billed early via a manual forMonth run): still move one
  // period on so the row is not billed twice for the same span.
  const periods = monthsBehind < 0 ? 1 : Math.floor(monthsBehind / step) + 1;
  return addCalendarMonths(currentNextBillOn, periods * step);
}

/** What one billing period of this row is worth per month (quarterly/3, annual/12). */
export function monthlyEquivalent(
  amount: number | string | null | undefined,
  cadenceRaw: string | null | undefined
): number {
  const cadence = readBillingCadence(cadenceRaw);
  // An unrecognised cadence cannot be priced; 0 (never NaN, never guessed as
  // monthly) keeps a dashboard total honest.
  if (cadence === null) return 0;
  if (cadence === "monthly") {
    // Pass-through, UNROUNDED: the snapshot/dashboard readers summed raw monthly
    // amounts before cadences existed, and must keep producing the same figure.
    const parsed = typeof amount === "number" ? amount : Number(amount);
    return amount === null || amount === undefined || !Number.isFinite(parsed) ? 0 : parsed;
  }
  return normalizeToMrr(amount, cadence);
}

/**
 * Customer-facing service period for an invoice line, starting at the billed
 * month: `2026-09` (monthly), `2026-09 through 2026-11` (quarterly),
 * `2027-05 through 2028-04` (annual).
 */
export function describeBillingPeriod(
  cadenceRaw: string | null | undefined,
  billedMonth: string
): string {
  const cadence = readBillingCadence(cadenceRaw) ?? DEFAULT_RECURRING_BILLING_CADENCE;
  if (cadence === "monthly" || !isMonthKey(billedMonth)) return billedMonth;
  const last = addCalendarMonths(`${billedMonth}-01`, MONTHS_PER_BILLING_PERIOD[cadence] - 1);
  return `${billedMonth} through ${monthOf(last)}`;
}

export const BILLING_CADENCE_LABELS: Readonly<Record<RecurringBillingCadence, string>> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

/** Noun for "total per ___" copy: month | quarter | year. */
export const BILLING_PERIOD_NOUN: Readonly<Record<RecurringBillingCadence, string>> = {
  monthly: "month",
  quarterly: "quarter",
  annual: "year",
};

/**
 * Cross-field rule for a form/inline save that names a cadence: quarterly/annual
 * need a real `next_bill_on`; monthly must not carry one. Returns a user-facing
 * message, or null when valid. `cadence` undefined = the save does not touch the
 * cadence (nothing to check here; the DB CHECK is the backstop).
 */
export function validateCadenceAnchorInput(
  cadence: string | null | undefined,
  nextBillOn: string | null | undefined
): string | null {
  if (cadence === undefined || cadence === null || cadence === "") return null;
  const parsed = readBillingCadence(cadence);
  if (parsed === null) return "Billing cadence must be monthly, quarterly or annual.";
  const hasAnchor = typeof nextBillOn === "string" && nextBillOn !== "";
  if (parsed === "monthly") {
    return hasAnchor ? "Next bill date only applies to quarterly or annual billing." : null;
  }
  if (!hasAnchor) return `${BILLING_CADENCE_LABELS[parsed]} billing needs a next bill date.`;
  if (!isIsoDate(nextBillOn) || addCalendarMonths(nextBillOn, 0) !== nextBillOn) {
    return "Next bill date must be a real calendar date.";
  }
  return null;
}

export interface LineTotals {
  cadence: RecurringBillingCadence;
  cadenceLabel: string;
  periodNoun: string;
  /** amount x quantity — what ONE invoice for this line charges. */
  periodTotal: number;
  /** The same line expressed per month (annual / 12, quarterly / 3). */
  monthlyEquivalent: number;
}

/** Totals the detail page shows: the per-period charge and its monthly (MRR) equivalent. */
export function describeLineTotals(row: {
  amount: number | string | null | undefined;
  quantity?: number | string | null;
  billing_cadence?: string | null;
}): LineTotals {
  const cadence = readBillingCadence(row.billing_cadence) ?? DEFAULT_RECURRING_BILLING_CADENCE;
  const amount = Number(row.amount);
  const quantity = Number(row.quantity) || 1;
  const periodTotal = Number.isFinite(amount) ? amount * quantity : 0;
  return {
    cadence,
    cadenceLabel: BILLING_CADENCE_LABELS[cadence],
    periodNoun: BILLING_PERIOD_NOUN[cadence],
    periodTotal,
    monthlyEquivalent: monthlyEquivalent(periodTotal, cadence),
  };
}
